import { Notification } from '@interfaces/notifications';
import { parseUtcTimestamp } from '@util/utc-timestamp';
import * as moment from 'moment';

/**
 * Parse schedule timestamps from API/DB.
 * Naive LocalDateTime is UTC wall-clock (cloud JVM + timestamptz). Offset/`Z` is an absolute instant.
 */
function parseOptionalDate(raw: string | Date | null | undefined): Date | null {
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  return parseUtcTimestamp(raw);
}

/**
 * datetime-local form value → UTC wall-clock for API / payload (no `Z`: Jackson LocalDateTime).
 * Cloud JVM is UTC; `send_at`/`expire_at` are timestamptz.
 */
export function serializeNotificationScheduleTimestamp(
  value: Date | null | undefined
): string | undefined {
  if (!value) {
    return undefined;
  }
  return moment(value).utc().format('YYYY-MM-DDTHH:mm:ss');
}

/** Scheduled / actual start: send_at, then payload.scheduling.start_at. */
export function getNotificationSendAt(notification: Notification): Date | null {
  const fromColumn = parseOptionalDate(notification.send_at);
  if (fromColumn) {
    return fromColumn;
  }
  return parseOptionalDate(notification.payload?.scheduling?.start_at);
}

/**
 * Scheduled end: expire_at, then payload.scheduling.end_at, then legacy payload.due_date.
 */
export function getNotificationExpireAt(notification: Notification): Date | null {
  const fromColumn = parseOptionalDate(notification.expire_at);
  if (fromColumn) {
    return fromColumn;
  }
  const fromScheduling = parseOptionalDate(notification.payload?.scheduling?.end_at);
  if (fromScheduling) {
    return fromScheduling;
  }
  return parseOptionalDate(notification.payload?.due_date);
}

/**
 * Mirrors backend filters: send_at <= now and (expire_at IS NULL OR expire_at > now).
 */
export function isNotificationWithinSchedule(
  notification: Notification,
  now: Date = new Date()
): boolean {
  const sendAt = getNotificationSendAt(notification);
  if (sendAt && sendAt.getTime() > now.getTime()) {
    return false;
  }

  const expireAt = getNotificationExpireAt(notification);
  if (expireAt && expireAt.getTime() <= now.getTime()) {
    return false;
  }

  return true;
}

/** Earliest future expireAt among notifications still within schedule, or null. */
export function getNextNotificationExpireAt(
  notifications: Notification[],
  now: Date = new Date()
): Date | null {
  let next: Date | null = null;
  const nowMs = now.getTime();

  for (const notification of notifications) {
    if (!isNotificationWithinSchedule(notification, now)) {
      continue;
    }
    const expireAt = getNotificationExpireAt(notification);
    if (!expireAt || expireAt.getTime() <= nowMs) {
      continue;
    }
    if (!next || expireAt.getTime() < next.getTime()) {
      next = expireAt;
    }
  }

  return next;
}
